import { createFileRoute } from "@tanstack/react-router";
import { GonzalAdsPage } from "@/components/calv/GonzalAdsPage";
import { jsonLdScript, breadcrumbSchema, pageHead } from "@/lib/seo";
import { SITE_URL } from "@/lib/locale";

export const Route = createFileRoute("/en/services/gonzalads")({
  head: () => {
    const h = pageHead("gonzalads", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
            jsonLdScript(
              breadcrumbSchema([
                { name: "Home", path: "/en" },
                { name: "Services", path: "/en/services" },
                { name: "Tools", path: "/en/services/tools" },
                { name: "GonzalAds", path: "/en/services/gonzalads" },
              ]),
            ),
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "GonzalAds",
              alternateName: "GonzalAds — Speedy Ad Management",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              description:
                "GonzalAds replaces the manual grind in the Meta Business Manager: AI-driven campaign creation, bulk engine, asset system with format variants, preset logic, and 24/7 automation rules.",
              url: `${SITE_URL}/en/services/gonzalads`,
              sameAs: ["https://gonzalads.com"],
              provider: { "@type": "Organization", name: "Calvarius GmbH", url: SITE_URL },
              offers: {
                "@type": "Offer",
                availability: "https://schema.org/InStock",
                description: "7-day free trial, no credit card required",
              },
            }),
          ],
    };
  },
  component: () => (
    <GonzalAdsPage
      lang="en"
      eyebrow="Tool · Meta Campaign Steering"
      headline={<>Meta Ads. <em>80% faster. Finally under control.</em></>}
      subline="GonzalAds replaces the manual grind in the Meta Business Manager with a coherent system: AI-driven campaign creation, rule-based bulk operations, crystal-clear creative analytics, performance automation 24/7 — all in one place."
      metaStrip={["OWN BRAND", "OWN DOMAIN: GONZALADS.COM", "7 DAYS FREE"]}
      breadcrumbs={[
        { label: "Home", to: "/en" },
        { label: "Services", to: "/en/services" },
        { label: "Tools", to: "/en/services/tools" },
        { label: "GonzalAds" },
      ]}
      standpunktKicker="Standpoint"
      standpunktHeadline={<>The Business Manager <em>does not scale.</em></>}
      standpunktParagraphs={[
        "The Meta Business Manager is a mature advertising platform – but it's optimized for single accounts, not scaled performance realities. As campaign volume grows, this becomes noticeable. Setup times extend. Repetitive work dominates daily operations. Placement errors arise from manual format handling. A central workflow tool for teams is entirely missing.",
        "GonzalAds was built to dissolve exactly these bottlenecks. Five pillars carry the system: an AI-driven Campaign Wizard that makes campaigns production-ready in roughly ten minutes. A Bulk Engine that generates hundreds of ads from a single rule — with correct placements and naming conventions. An asset system with automatic format variants and creative fingerprinting. Preset logic for reusable settings. And Automation Rules that steer campaigns 24/7 on KPIs like ROAS or CPA.",
        "GonzalAds is a proprietary development by Calvarius — born from the requirements of our performance mandates, evolved into a standalone brand with its own platform. The web app is available for a seven-day free trial at gonzalads.com, no credit card required. In the Calvarius mandate context, we deploy GonzalAds as part of campaign steering and handle setup, onboarding, and integration into existing tracking and creative workflows.",
      ]}
      pillarsKicker="Capabilities"
      pillarsHeadline={<>The five <em>pillars.</em></>}
      pillars={[
        { num: "01", name: "Campaign Wizard", desc: "Guided campaign creation with AI-generated copy. From idea to live campaign in about 10 minutes." },
        { num: "02", name: "Bulk Engine", desc: "Rule-based combination of images, copy, and targeting. 100+ ads from a single rule — with correct placements and naming." },
        { num: "03", name: "Asset System", desc: "Images, videos, copy centrally managed. Automatic format variants (1:1, 9:16, 16:9). Creative fingerprinting prevents duplicates." },
        { num: "04", name: "Preset Logic", desc: "Define targeting, campaign settings, and creative templates once — reuse without limit." },
        { num: "05", name: "Automation", desc: "Performance rules on CPA, ROAS, spend, CTR. Reaction time under one minute. Pauses, activates, scales — even at night." },
      ]}
      uspsKicker="Differentiation"
      uspsHeadline={<>What makes GonzalAds <em>different.</em></>}
      usps={[
        { num: "01", name: "Never wrong formats", detail: "Placement Safety Guard validates every campaign before upload. Stories get 9:16, Feed gets 1:1 — automatically." },
        { num: "02", name: "80–90% time savings", detail: "Campaigns are built in minutes instead of hours. Wizard plus Bulk Engine replace hundreds of manual clicks in the Business Manager." },
        { num: "03", name: "Format intelligence", detail: "Three format variants are maintained per asset. Meta receives the right file for each placement automatically." },
        { num: "04", name: "Creative fingerprinting", detail: "Every asset gets a unique fingerprint. No duplicate uploads, clear performance attribution." },
        { num: "05", name: "AI copy without CTAs", detail: "AI generates headlines and primary texts that comply with Meta policies — no direct sales CTAs in the headline." },
        { num: "06", name: "Team-ready from day one", detail: "Roles, rights, shared ad accounts, and agency token hierarchy for clean collaboration." },
        { num: "07", name: "Auto-sync analytics", detail: "Data is pre-warmed in the background — reports are ready the moment you log in." },
        { num: "08", name: "Automation Rules 24/7", detail: "Rules on CPA, ROAS, spend, CTR. Reaction time under one minute, continuously." },
      ]}
      compareKicker="Direct comparison"
      compareHeadline={<>Business Manager <em>vs. GonzalAds.</em></>}
      compareLeftTitle="Business Manager"
      compareRightTitle="GonzalAds"
      compareRows={[
        { left: "Hours for a launch", right: "Campaigns live in minutes" },
        { left: "Hundreds of clicks per campaign", right: "5 clicks to a finished campaign" },
        { left: "Placement errors from manual setup", right: "No more wrong formats" },
        { left: "No central team workflow tool", right: "Team roles and rights management" },
      ]}
      valueKicker="Value"
      valueHeadline={<>What GonzalAds <em>delivers.</em></>}
      values={[
        { strong: "80–90% time savings per campaign.", rest: "What takes hours in Meta Business Manager runs in minutes in the GonzalAds Wizard." },
        { strong: "Zero placement errors.", rest: "Pre-upload validation eliminates one of the most common sources of error in manual campaign setup." },
        { strong: "Scaling without quality loss.", rest: "Multi-account, multi-tenant, team roles — performance marketing setups that have outgrown the native Manager stay manageable with GonzalAds." },
        { strong: "Performance automation around the clock.", rest: "Rules trigger in under a minute, continuously. Campaigns react to KPI changes without waiting for a human to check in." },
        { strong: "Instantly available analytics.", rest: "Data is pre-warmed in the background — reports are there when you log in, not after loading multiple dashboards." },
      ]}
      audienceKicker="Audience"
      audienceHeadline={<>Who GonzalAds is <em>built for.</em></>}
      audience={[
        { strong: "In-house performance teams with Meta focus", rest: "where daily effort in the Business Manager visibly becomes the bottleneck" },
        { strong: "Advertising agencies with multiple mandates", rest: "setup acceleration, multi-account overview, and team roles are the biggest lever here" },
        { strong: "Performance marketers losing too much time in the Business Manager", rest: "who need a faster, more precise workflow" },
        { strong: "Mandates with continuous test and scaling demand", rest: "where the native interface simply can't keep up beyond a certain volume" },
      ]}
      calvariusKicker="Origin"
      calvariusHeadline={<>Behind GonzalAds stands <em>Calvarius.</em></>}
      calvariusBody="GonzalAds is a proprietary development by Calvarius GmbH — born from the requirements of our performance marketing mandates since 2013. We use GonzalAds daily, develop it continuously, and operate it as a standalone brand at gonzalads.com."
      ctaKicker="Two paths"
      ctaHeadline={<>Try it yourself or <em>use it in your mandate.</em></>}
      cardA={{
        eyebrow: "Self-service",
        headline: "Try GonzalAds free for 7 days",
        body: "Full feature set. No credit card. Directly at gonzalads.com.",
        button: "To the product site",
      }}
      cardB={{
        eyebrow: "Mandate integration",
        headline: "GonzalAds as part of your performance mandate",
        body: "We deploy GonzalAds as part of campaign steering and handle setup, onboarding, and integration into existing tracking and creative workflows.",
        button: "Talk to our team",
        to: "/en/contact",
      }}
    />
  ),
});
